import type { Update } from '@ngrx/entity';

import type { NormalizedColumnEntity, NormalizedDefaultColumnEntity, NormalizedSpecificColumnEntity } from '@web/types';

import { createDefaultColumn, createSpecificColumn } from '@sites/entity-core';

import { getUuid } from '@common/helpers';
import { TRANSPARENT_VALUE } from '@components/sp-color-picker/src/constants/color-picker-presets.const';

type ColumnSeed = {
    /** Owning section. */
    parentId?: string;
    children?: { id: string; schema: string }[];
};

/**
 * Seeds come from `@sites/entity-core` so the store and the schema the entity
 * is validated against cannot drift apart — only identity, the parent link and
 * the child list are filled in here.
 */
export const columnInstance = ({ parentId, children = [] }: ColumnSeed = {}): NormalizedDefaultColumnEntity => ({
    ...createDefaultColumn({ transparentColor: TRANSPARENT_VALUE }),
    id: getUuid(),
    parentId,
    children,
});

export const specificColumnInstance = ({
    parentId,
    children = [],
}: ColumnSeed = {}): NormalizedSpecificColumnEntity => ({
    ...createSpecificColumn(),
    id: getUuid(),
    parentId,
    children,
});

/**
 * Builds an entity-adapter update for one column's `props`.
 *
 * `Update<NormalizedColumnEntity>` widens `changes.props` to the union of all
 * three subtypes, which needs a cast — and a bare cast at each call site once let
 * a plain number be written where the width node belongs. Taking `props` typed
 * against the column being updated puts the check back where it matters and
 * leaves exactly one cast, here.
 */
export function columnPropsUpdate<T extends { id: string; props: object }>(
    column: T,
    props: T['props']
): Update<NormalizedColumnEntity> {
    return { id: column.id, changes: { props } as Partial<NormalizedColumnEntity> };
}
